import { Formik } from 'formik'
import { dayjsLondon } from 'lib/dayjs'
import { H, P, Wrapper } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import styled from 'styled-components'

import MessageBox from 'components/message-box'
import Checkbox from 'components/form/checkbox'
import { noop } from 'lib/functions/noop'
import { RESOURCES, UseUpdateArrayResourceResult } from 'api'
import { WillStorageRecord } from 'lib/models/will-storage-record'

function formatWillReference(willReference: string) {
  return willReference.slice(0, 3) + ' ' + willReference.slice(3)
}

const StyledHeadline = styled(P)`
  font-weight: ${FONT.WEIGHT.BOLD};
  margin-bottom: ${GTR.XXS};
`
const StyledIdentifier = styled(P)`
  font-weight: ${FONT.WEIGHT.BOLD};
`

const StyledCompletedByText = styled(P)`
  padding-left: 45px;
`

type Props = {
  record: WillStorageRecord
  updateMutation: UseUpdateArrayResourceResult<WillStorageRecord>
}

const WillStorageRecordBlock = ({ record, updateMutation }: Props) => {
  const {
    storageCode,
    willReference,
    purchasedAt,
    receivedAt,
    receivedByAdminUser,
  } = record.attributes
  const isReceived = !!receivedAt

  const handleSave = ({ value }: { value: string }) => {
    if (value !== 'true') return

    updateMutation.mutateAsync({
      id: record.id,
      resource: RESOURCES.WILL_STORAGE_RECORDS,
      endpoint: `/api/will-storage/${record.id}/receive`,
      attributes: {},
    })
  }

  return (
    <Wrapper
      margin={['M', 0]}
      data-testid={`will-storage-record-${storageCode}`}
    >
      <H size="S" margin={[0, 0, 'XS']}>
        Storage code
      </H>
      <StyledIdentifier
        size="M"
        color={COLOR.GREY.DARK}
        margin={[0, 0, 'M']}
        strong
      >
        {storageCode}
      </StyledIdentifier>
      {willReference && (
        <>
          <H size="S" margin={[0, 0, 'XS']}>
            Previous will reference
          </H>
          <StyledIdentifier
            size="M"
            color={COLOR.GREY.DARK}
            margin={[0, 0, 'M']}
            strong
          >
            {formatWillReference(willReference)}
          </StyledIdentifier>
        </>
      )}

      {isReceived ? (
        <MessageBox success margin={['L', 0]} stretch>
          <StyledHeadline color={COLOR.BLACK}>
            This will is stored securely inside the Farewill Vault
          </StyledHeadline>
          <P size="S" color={COLOR.GREY.DARK}>
            Stored since {dayjsLondon(receivedAt).format('D MMMM YYYY')}
          </P>
        </MessageBox>
      ) : (
        <MessageBox warning margin={['L', 0]} stretch>
          <StyledHeadline color={COLOR.BLACK}>
            Customer has paid for storage but we haven't received their will yet
          </StyledHeadline>
          <P size="S" color={COLOR.GREY.DARK}>
            Purchased on {dayjsLondon(purchasedAt).format('D MMMM YYYY')}
          </P>
        </MessageBox>
      )}

      <H size="S" margin={[0, 0, 'XS']}>
        Storing the will
      </H>
      <P margin={['S', 0]}>
        Place the will inside the <strong>Farewill Vault</strong>, then tick to
        confirm. This can't be undone.
      </P>

      <Formik initialValues={{ isReceived }} onSubmit={noop} enableReinitialize>
        <Checkbox
          color="green"
          name="isReceived"
          disabled={isReceived}
          label="Will is stored in the Farewill Vault"
          handleSave={handleSave}
        />
      </Formik>
      {isReceived && receivedByAdminUser && (
        <StyledCompletedByText
          size="S"
          color={COLOR.GREY.DARK}
          margin={[GTR.XXS, 0, 0]}
        >
          Completed by {receivedByAdminUser.name} on{' '}
          {dayjsLondon(receivedAt).format('D MMMM YYYY, HH:mm')}.
        </StyledCompletedByText>
      )}
    </Wrapper>
  )
}

export default WillStorageRecordBlock
